import { useState, useEffect, useMemo, useRef } from 'react';
import { getFirestore, query, collection, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore/lite';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { atom, useRecoilState } from 'recoil';
import { useMutation, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { usePrevious } from '@bluframe/grapple';
import isEqual from 'lodash/fp/isEqual';
import { UAParser } from 'ua-parser-js';
import axios from 'axios';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID
};
var app = initializeApp(firebaseConfig);
var db = getFirestore(app);
getAuth(app);
getFunctions(app);
getStorage(app);
getAnalytics();

var getBookOrderByWebReference = function (webReference) { return __awaiter(void 0, void 0, void 0, function () {
    var q, bookOrderByWebReferenceSnapshot, bookOrderByWebReferenceList;
    var _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                q = query(collection(db, "book_orders"), where("quote_ref", "==", webReference));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                bookOrderByWebReferenceSnapshot = _b.sent();
                bookOrderByWebReferenceList = bookOrderByWebReferenceSnapshot.docs.map(function (doc) { return doc.data(); });
                return [2 /*return*/, (_a = bookOrderByWebReferenceList === null || bookOrderByWebReferenceList === void 0 ? void 0 : bookOrderByWebReferenceList[0]) !== null && _a !== void 0 ? _a : null];
        }
    });
}); };

var useBookOrderByWebReference = function (_a) {
    var webReference = _a.webReference;
    var _b = __read(useState(null), 2), bookOrderByWebReference = _b[0], setBookOrderByWebReference = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    var getBookOrderByWebReference$1 = function () { return __awaiter(void 0, void 0, void 0, function () {
        var response;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setError("");
                    setLoading(true);
                    return [4 /*yield*/, getBookOrderByWebReference(webReference)];
                case 1:
                    response = _a.sent();
                    setLoading(false);
                    setBookOrderByWebReference(response);
                    if (response) {
                        setError(null);
                    }
                    else {
                        setError("Unable to load booking types");
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    return [
        getBookOrderByWebReference$1,
        {
            bookOrderByWebReference: bookOrderByWebReference,
            error: error,
            loading: loading
        }
    ];
};

var getBookingTypes = function (db) { return __awaiter(void 0, void 0, void 0, function () {
    var q, bookingTypesSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "booking_types"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                bookingTypesSnapshot = _a.sent();
                return [2 /*return*/, bookingTypesSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var bookingTypesState = atom({
    default: [],
    key: "bookingTypes"
});
var useBookingTypes = function (_a) {
    var db = _a.db;
    var _b = __read(useRecoilState(bookingTypesState), 2), bookingTypes = _b[0], setBookingTypes = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getBookingTypes(db)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setBookingTypes(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load booking types");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [db, setLoading, setBookingTypes]);
    return {
        error: error,
        loading: loading,
        bookingTypes: bookingTypes
    };
};

var useCalculateTotal = function (_a) {
    var answers = _a.answers, _b = _a.callOutFee, callOutFee = _b === void 0 ? 0 : _b, items = _a.items, questions = _a.questions, _c = _a.surgeMultiplier, surgeMultiplier = _c === void 0 ? 1 : _c;
    return useMemo(function () {
        var _a, _b, _c, _d, _e, _f;
        var total = 0;
        total = items.reduce(function (acc, item) {
            if (item.type === "dismantle" && item.dismantling_fee) {
                return acc + item.dismantling_fee * item.count;
            }
            if (item.type === "disconnect" && item.disconnection_fee) {
                return acc + item.disconnection_fee * item.count;
            }
            return (acc +
                item.price_inc_vat * item.count * surgeMultiplier);
        }, 0);
        // If waste is outside give discount
        if ((answers === null || answers === void 0 ? void 0 : answers[12]) &&
            answers[12].answer ===
                ((_a = questions === null || questions === void 0 ? void 0 : questions.find(function (question) { return question.rule === 12; })) === null || _a === void 0 ? void 0 : _a.options[0]))
            total -= (_b = questions === null || questions === void 0 ? void 0 : questions.find(function (question) { return question.rule === 12; })) === null || _b === void 0 ? void 0 : _b.charge.split(", ")[0];
        // Get charge based on floor
        if (answers === null || answers === void 0 ? void 0 : answers[1]) {
            var index = (_c = questions === null || questions === void 0 ? void 0 : questions.find(function (question) { return question.rule === 1; })) === null || _c === void 0 ? void 0 : _c.options.findIndex(function (option) { return option === answers[1].answer; });
            total += parseInt((_d = questions === null || questions === void 0 ? void 0 : questions.find(function (question) { return question.rule === 1; })) === null || _d === void 0 ? void 0 : _d.charge.split(",")[index]);
        }
        // Get charge based on distance
        var count;
        if (answers === null || answers === void 0 ? void 0 : answers[3]) {
            count = (_e = questions === null || questions === void 0 ? void 0 : questions.find(function (question) { return question.rule === 3; })) === null || _e === void 0 ? void 0 : _e.options.findIndex(function (option) { return option === answers[3].answer; });
        }
        else {
            count = 0;
        }
        if (questions) {
            total += parseInt((_f = questions === null || questions === void 0 ? void 0 : questions.find(function (question) { return question.rule === 3; })) === null || _f === void 0 ? void 0 : _f.charge.split(",")[count]);
        }
        return total + callOutFee * surgeMultiplier;
    }, [answers, callOutFee, items, questions, surgeMultiplier]);
};

var useCalculateTotalWithCoupons = function (_a) {
    var _b = _a.dayFee, dayFee = _b === void 0 ? 0 : _b, timeSlotFee = _a.timeSlotFee, quoteAmount = _a.quoteAmount, coupon = _a.coupon, _c = _a.dayOfWeek, dayOfWeek = _c === void 0 ? "" : _c;
    return useMemo(function () {
        var result = 0;
        var discount = 0;
        if (!coupon) {
            result = quoteAmount + timeSlotFee + dayFee;
        }
        else {
            var getResult = function () {
                var _a, _b;
                switch (coupon.type) {
                    case "percentage":
                        switch (coupon.limit_to) {
                            case "time_slot_fee":
                                discount = (timeSlotFee / 100) * coupon.value;
                                return dayFee + (timeSlotFee - discount) + quoteAmount;
                            case "weekend_fee":
                                if ((((_a = coupon.limit_to_weekend_day) === null || _a === void 0 ? void 0 : _a.includes("sunday")) &&
                                    dayOfWeek === "Sun") ||
                                    (((_b = coupon.limit_to_weekend_day) === null || _b === void 0 ? void 0 : _b.includes("saturday")) &&
                                        dayOfWeek === "Sat")) {
                                    discount = (dayFee / 100) * coupon.value;
                                    return timeSlotFee + (dayFee - discount) + quoteAmount;
                                }
                                return dayFee + timeSlotFee + quoteAmount;
                            case "items":
                                discount = (quoteAmount / 100) * coupon.value;
                                return timeSlotFee + dayFee + (quoteAmount - discount);
                            default:
                                // Add percentage to all
                                var total = dayFee + timeSlotFee + quoteAmount;
                                discount = (total / 100) * coupon.value;
                                return (total - discount).toFixed(2);
                        }
                    case "fixed_amount":
                        switch (coupon.limit_to) {
                            case "time_slot_fee":
                                return dayFee + quoteAmount;
                            default:
                                return dayFee + timeSlotFee + quoteAmount;
                        }
                    default:
                        return dayFee + timeSlotFee + quoteAmount;
                }
            };
            result = getResult();
        }
        return { discount: discount, total: Number(Number(result).toFixed(2)) };
    }, [dayFee, timeSlotFee, quoteAmount, coupon, dayOfWeek]);
};

var getCallOutFee = function (db) { return __awaiter(void 0, void 0, void 0, function () {
    var q, callOutFeeSnapshot;
    var _a, _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                q = query(collection(db, "service_pricing"), where("litta_service_linked", "==", "Call Out Fee"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                callOutFeeSnapshot = _c.sent();
                return [2 /*return*/, (_b = (_a = callOutFeeSnapshot.docs.map(function (doc) { return doc.data(); })) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null];
        }
    });
}); };

var callOutFeeState = atom({
    default: 0,
    key: "callOutFee"
});
var useCallOutFee = function (_a) {
    var collectionItems = _a.collectionItems, db = _a.db;
    var _b = __read(useRecoilState(callOutFeeState), 2), callOutFee = _b[0], setCallOutFee = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getCallOutFee(db)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setCallOutFee(response === null || response === void 0 ? void 0 : response.price_inc_vat);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load call out fee");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        var filteredItems = collectionItems.filter(function (item) { return item.type !== "dismantle" && item.type !== "disconnect"; });
        var hasOnlyItems = !filteredItems.some(function (item) { return item.type !== "item"; });
        if (collectionItems.length === 0 || !hasOnlyItems) {
            setCallOutFee(0);
            return;
        }
        getData();
    }, [collectionItems, db, setLoading, setCallOutFee]);
    return {
        callOutFee: callOutFee,
        error: error,
        loading: loading
    };
};

var useDismantleDisconnectService = function (_a) {
    var actionType = _a.actionType, item = _a.item;
    var service = {
        record_id: item.record_id,
        litta_parent_item: item.litta_service_linked
    };
    if (actionType === "disconnect") {
        return __assign(__assign({}, service), { disconnection_fee: item.disconnection_fee ? item.disconnection_fee : 0, litta_service_linked: "disconnect ".concat(item.litta_service_linked), type: "disconnect" });
    }
    return __assign(__assign({}, service), { dismantling_fee: item.dismantling_fee ? item.dismantling_fee : 0, litta_service_linked: "dismantle ".concat(item.litta_service_linked), type: "dismantle" });
};

var emailVerify = function (_a) {
    var email = _a.email, source = _a.source;
    return __awaiter(void 0, void 0, void 0, function () {
        var functions, emailVerifyApi, response;
        return __generator(this, function (_b) {
            functions = getFunctions();
            emailVerifyApi = httpsCallable(functions, "checkEmail");
            response = emailVerifyApi({ email: email, source: source });
            return [2 /*return*/, response];
        });
    });
};

var useEmailVerify = function (_a) {
    var _b;
    var email = _a.email, source = _a.source;
    var _c = useMutation(emailVerify), data = _c.data, isError = _c.isError, isLoading = _c.isLoading, mutate = _c.mutate, mutateAsync = _c.mutateAsync;
    var debouncedMutate = debounce(mutate, 1000);
    useEffect(function () {
        if (email && source) {
            debouncedMutate({ email: email, source: source });
        }
    }, [email, source]);
    return {
        data: data,
        isError: isError,
        isLoading: isLoading,
        isValid: data === undefined || email === ""
            ? undefined
            : ((_b = (data === null || data === void 0 ? void 0 : data.data).result) === null || _b === void 0 ? void 0 : _b.verdict) !== "Invalid",
        mutate: mutate,
        mutateAsync: mutateAsync
    };
};

var getErrorMessages = function (platform) { return __awaiter(void 0, void 0, void 0, function () {
    var q, errorMessagesSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "error_message"), where("platform", "array-contains", platform));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                errorMessagesSnapshot = _a.sent();
                return [2 /*return*/, errorMessagesSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var errorMessagesState = atom({
    default: [],
    key: "errorMessages"
});
var useErrorMessages = function (_a) {
    var platform = _a.platform;
    var _b = __read(useRecoilState(errorMessagesState), 2), errorMessages = _b[0], setErrorMessages = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getErrorMessages(platform)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setErrorMessages(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load error messages");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [setLoading, setErrorMessages]);
    return {
        error: error,
        errorMessages: errorMessages,
        loading: loading
    };
};

var getFailReasons = function (db, platform) { return __awaiter(void 0, void 0, void 0, function () {
    var q, failReasonsSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "fail_reasons"), where("platform", "array-contains", platform), where("active", "==", true), orderBy("priority"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                failReasonsSnapshot = _a.sent();
                return [2 /*return*/, failReasonsSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var failReasonsState = atom({
    default: [],
    key: "failReasons"
});
var useFailReasons = function (_a) {
    var platform = _a.platform;
    var _b = __read(useRecoilState(failReasonsState), 2), failReasons = _b[0], setFailReasons = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getFailReasons(db, platform)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setFailReasons(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load fail reasons");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [db, setLoading, setFailReasons]);
    return {
        error: error,
        failReasons: failReasons,
        loading: loading
    };
};

var getHolidays = function (db) { return __awaiter(void 0, void 0, void 0, function () {
    var q, holidaysSnapshot, holidaysList;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "holidays"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                holidaysSnapshot = _a.sent();
                holidaysList = holidaysSnapshot.docs.map(function (doc) {
                    var document = doc.data();
                    var date = new Date(0);
                    date.setUTCSeconds(document.date.seconds);
                    return __assign(__assign({}, document), { date: date });
                });
                return [2 /*return*/, holidaysList !== null && holidaysList !== void 0 ? holidaysList : []];
        }
    });
}); };

var holidaysState = atom({
    default: [],
    key: "holidays"
});
var useHolidays = function (_a) {
    var db = _a.db;
    var _b = __read(useRecoilState(holidaysState), 2), holidays = _b[0], setHolidays = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getHolidays(db)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setHolidays(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load holidays");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [db, setLoading, setHolidays]);
    return {
        error: error,
        loading: loading,
        holidays: holidays
    };
};

var getImagesRequest = function () { return __awaiter(void 0, void 0, void 0, function () {
    var q, imagesRequestSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "request_images"), orderBy("priority"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                imagesRequestSnapshot = _a.sent();
                return [2 /*return*/, imagesRequestSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var imagesRequestState = atom({
    default: [],
    key: "imagesRequest"
});
var useImagesRequest = function () {
    var _a = __read(useRecoilState(imagesRequestState), 2), imagesRequest = _a[0], setImagesRequest = _a[1];
    var _b = __read(useState(false), 2), loading = _b[0], setLoading = _b[1];
    var _c = __read(useState(null), 2), error = _c[0], setError = _c[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getImagesRequest()];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setImagesRequest(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load image requests");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [setLoading, setImagesRequest]);
    return {
        error: error,
        imagesRequest: imagesRequest,
        loading: loading
    };
};

var getPageContent = function () { return __awaiter(void 0, void 0, void 0, function () {
    var q, pageContentSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "page_content"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                pageContentSnapshot = _a.sent();
                return [2 /*return*/, pageContentSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var pageContentState = atom({
    default: [],
    key: "pageContent"
});
var usePageContent = function () {
    var _a = __read(useRecoilState(pageContentState), 2), pageContent = _a[0], setPageContent = _a[1];
    var _b = __read(useState(false), 2), loading = _b[0], setLoading = _b[1];
    var _c = __read(useState(null), 2), error = _c[0], setError = _c[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getPageContent()];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setPageContent(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load page content");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [setLoading, setPageContent]);
    return {
        error: error,
        loading: loading,
        pageContent: pageContent
    };
};

var getPageHeaders = function () { return __awaiter(void 0, void 0, void 0, function () {
    var q, pageHeadersSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "page_headers"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                pageHeadersSnapshot = _a.sent();
                return [2 /*return*/, pageHeadersSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var pageHeadersState = atom({
    default: [],
    key: "pageHeaders"
});
var usePageHeaders = function () {
    var _a = __read(useRecoilState(pageHeadersState), 2), pageHeaders = _a[0], setPageHeaders = _a[1];
    var _b = __read(useState(false), 2), loading = _b[0], setLoading = _b[1];
    var _c = __read(useState(null), 2), error = _c[0], setError = _c[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getPageHeaders()];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setPageHeaders(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load pages headers");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [setLoading, setPageHeaders]);
    return {
        error: error,
        pageHeaders: pageHeaders,
        loading: loading
    };
};

var calculatePayout = function (_a) {
    var items = _a.items;
    return __awaiter(void 0, void 0, void 0, function () {
        var functions, calculatePayoutCallable, response;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    functions = getFunctions();
                    calculatePayoutCallable = httpsCallable(functions, "calculate_payout_onCall");
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, calculatePayoutCallable({ items: items })];
                case 2:
                    response = _b.sent();
                    return [2 /*return*/, {
                            items: response.data.items,
                            recommendedCustomerSurgePrice: response.data
                                .recommendedCustomerSurgePrice,
                            recommendedPartnerPayout: response.data.recommendedPartnerPayout
                        }];
                case 3:
                    _b.sent();
                    return [2 /*return*/, {
                            items: [],
                            recommendedCustomerSurgePrice: 0,
                            recommendedPartnerPayout: 0
                        }];
                case 4: return [2 /*return*/];
            }
        });
    });
};

var defaultPayoutsState = {
    items: [],
    recommendedCustomerSurgePrice: 0,
    recommendedPartnerPayout: 0
};
var payoutsState = atom({
    default: defaultPayoutsState,
    key: "payouts"
});
var usePayouts = function (_a) {
    var items = _a.items;
    var _b = __read(useRecoilState(payoutsState), 2), payouts = _b[0], setPayouts = _b[1];
    var previousCollectionItems = usePrevious(items);
    useEffect(function () {
        if (!items) {
            return;
        }
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, calculatePayout({ items: items })];
                    case 1:
                        data = _a.sent();
                        setPayouts(data);
                        return [2 /*return*/];
                }
            });
        }); };
        if (!isEqual(items)(previousCollectionItems)) {
            if (items.length) {
                getData();
            }
            else {
                setPayouts(defaultPayoutsState);
            }
        }
    }, [items, previousCollectionItems, setPayouts]);
    return {
        payouts: payouts
    };
};

var getPostCodesSurge = function (db, outerPostcode) { return __awaiter(void 0, void 0, void 0, function () {
    var q, postCodesSurgeSnapshot, postCodesSurgeList;
    var _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                q = query(collection(db, "postCodes_surge"), where("outer_postcode", "==", outerPostcode));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                postCodesSurgeSnapshot = _b.sent();
                postCodesSurgeList = postCodesSurgeSnapshot.docs.map(function (doc) { return doc.data(); });
                return [2 /*return*/, (_a = postCodesSurgeList === null || postCodesSurgeList === void 0 ? void 0 : postCodesSurgeList[0]) !== null && _a !== void 0 ? _a : null];
        }
    });
}); };

var postCodesSurgeState = atom({
    default: null,
    key: "postCodesSurge"
});
var usePostCodesSurge = function (_a) {
    var onUpdatePostCodesSurge = _a.onUpdatePostCodesSurge, selectedAddress = _a.selectedAddress;
    var _b = __read(useRecoilState(postCodesSurgeState), 2), postCodesSurge = _b[0], setPostCodesSurge = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    var outerPostcode = useMemo(function () {
        if (!selectedAddress) {
            return null;
        }
        var addressParts = selectedAddress.split(",");
        var postCode = addressParts[addressParts.length - 1];
        var _a = __read(postCode.trim().split(" "), 1), part = _a[0];
        return part;
    }, [selectedAddress]);
    useEffect(function () {
        if (outerPostcode) {
            var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
                var response;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            setError("");
                            setLoading(true);
                            return [4 /*yield*/, getPostCodesSurge(db, outerPostcode)];
                        case 1:
                            response = _a.sent();
                            setLoading(false);
                            setPostCodesSurge(response);
                            if (onUpdatePostCodesSurge) {
                                onUpdatePostCodesSurge(response);
                            }
                            if (response) {
                                setError(null);
                            }
                            else {
                                setError("Unable to load fail reasons");
                            }
                            return [2 /*return*/];
                    }
                });
            }); };
            getData();
        }
    }, [outerPostcode]);
    return {
        error: error,
        loading: loading,
        postCodesSurge: postCodesSurge
    };
};

var getPromoCode = function (code) { return __awaiter(void 0, void 0, void 0, function () {
    var q, promoCodesSnapshot, promoCodesList;
    var _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                q = query(collection(db, "promo_codes"), where("code", "==", code));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                promoCodesSnapshot = _b.sent();
                promoCodesList = promoCodesSnapshot.docs.map(function (doc) {
                    return doc.data();
                });
                return [2 /*return*/, (_a = promoCodesList === null || promoCodesList === void 0 ? void 0 : promoCodesList[0]) !== null && _a !== void 0 ? _a : null];
        }
    });
}); };

var promoCodeState = atom({
    default: null,
    key: "promoCode"
});
var usePromoCode = function (_a) {
    var code = _a.code;
    var _b = __read(useRecoilState(promoCodeState), 2), promoCode = _b[0], setPromoCode = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    var timerRef = useRef(null);
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!code) return [3 /*break*/, 2];
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getPromoCode(code)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setPromoCode(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("This discount code is invalid");
                            setTimeout(function () {
                                setError(null);
                            }, 3000);
                        }
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        if (code) {
            getData();
        }
    }, [code, setLoading, setPromoCode]);
    useEffect(function () {
        if (code === "") {
            setPromoCode(null);
        }
    }, [code]);
    useEffect(function () {
        // Clear the interval when the component unmounts
        return function () { return clearTimeout(timerRef.current); };
    }, []);
    return {
        error: error,
        loading: loading,
        promoCode: promoCode
    };
};

var usePurchaseDataLayer = function (_a) {
    var affiliation = _a.affiliation, _b = _a.confirmation, confirmation = _b === void 0 ? false : _b, recordId = _a.recordId, setLoadingSession = _a.setLoadingSession, _c = _a.shouldTriggerDataLayer, shouldTriggerDataLayer = _c === void 0 ? true : _c;
    var functions = getFunctions();
    var getRecordById = httpsCallable(functions, "getRecordById");
    var _d = __read(useState(null), 2), record = _d[0], setRecord = _d[1];
    var hasAlreadyTriggeredDataLayer = false;
    useEffect(function () {
        if (recordId) {
            if (!confirmation && setLoadingSession) {
                setLoadingSession(true);
            }
            var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
                var data, products, disconnect;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, getRecordById({ recordId: recordId })];
                        case 1:
                            data = (_a.sent()).data.data;
                            setRecord(data);
                            if (confirmation &&
                                !hasAlreadyTriggeredDataLayer &&
                                shouldTriggerDataLayer) {
                                window.dataLayer = window.dataLayer || [];
                                products = data.items.map(function (item) {
                                    return {
                                        name: item["LITTA Service"],
                                        price: item.price_inc_vat,
                                        quantity: item.count
                                    };
                                });
                                if (data.disconnect.length) {
                                    disconnect = data.disconnect.map(function (item) {
                                        return {
                                            name: item.litta_service_linked,
                                            price: item.disconnection_fee,
                                            quantity: item.count
                                        };
                                    });
                                    products = products.concat(disconnect);
                                }
                                if (data.dismantle.length) {
                                    products = products.concat(data.dismantle.map(function (item) {
                                        return {
                                            name: item.litta_service_linked,
                                            price: item.dismantling_fee,
                                            quantity: item.count
                                        };
                                    }));
                                }
                                if (data.questionTotal) {
                                    products.push({
                                        name: "Accessibility fee",
                                        price: data.questionTotal
                                    });
                                }
                                if (data.TIME_SLOT_FEE) {
                                    products.push({
                                        name: "Time slot fee",
                                        price: data.TIME_SLOT_FEE
                                    });
                                }
                                if (data.WEEK_DAY_FEE) {
                                    products.push({ name: "Weekend fee", price: data.WEEK_DAY_FEE });
                                }
                                window.dataLayer.push({
                                    event: "purchase",
                                    ecommerce: {
                                        purchase: {
                                            actionField: {
                                                id: recordId,
                                                affiliation: affiliation,
                                                revenue: data.total
                                            },
                                            products: products
                                        }
                                    }
                                });
                            }
                            if (!confirmation && setLoadingSession) {
                                setTimeout(function () {
                                    setLoadingSession(false);
                                }, 500);
                            }
                            return [2 /*return*/];
                    }
                });
            }); };
            getData();
        }
        return function () {
            if (shouldTriggerDataLayer) {
                hasAlreadyTriggeredDataLayer = true;
            }
        };
    }, [hasAlreadyTriggeredDataLayer, shouldTriggerDataLayer]);
    return record;
};

var getQuestions = function (db, platform) { return __awaiter(void 0, void 0, void 0, function () {
    var q, questionsSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "checkout_rules"), where("platform", "array-contains", platform));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                questionsSnapshot = _a.sent();
                return [2 /*return*/, questionsSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var getDataModel = function (data) {
    var result = data.filter(function (item) { return item.active && item.question_type !== "address"; });
    var condensed_array = result.filter(function (item) { return !item.question_group_id; });
    var grouped_array = result.filter(function (item) {
        return Boolean(item.question_group_id);
    });
    var groups = grouped_array.reduce(function (groups, item) {
        var group = groups[item.question_group_id] || [];
        group.push(item);
        groups[item.question_group_id] = group;
        return groups;
    }, {});
    Object.entries(groups).forEach(function (group) {
        if (group[1].some(function (item) { return Boolean(item.parent_record_id); })) {
            var parent_1 = group[1].find(function (question) { return !question.parent_record_id; });
            parent_1.dependant = group[1].find(function (question) {
                return parent_1 && question.parent_record_id === parent_1.record_id;
            });
            parent_1.dependant.dependant = group[1].find(function (question) {
                return (parent_1 && question.parent_record_id === parent_1.dependant.record_id);
            });
            condensed_array.push(parent_1);
        }
    });
    return condensed_array
        .map(function (item) {
        var _a, _b;
        return __assign(__assign(__assign(__assign(__assign(__assign({ title: item.question, rule: item.rule, recordId: item === null || item === void 0 ? void 0 : item.record_id }, (item.question_subtitle && { subtitle: item.question_subtitle })), (item.required && { required: true })), (item.default_option && { default_option: item.default_option })), { type: item.question_type }), (item.dependant && {
            dependant: __assign(__assign(__assign(__assign(__assign({ title: item.dependant.question, rule: item.dependant.rule, recordId: (_a = item.dependant) === null || _a === void 0 ? void 0 : _a.record_id }, (item.dependant.default_option && {
                default_option: item.dependant.default_option
            })), { type: item.dependant.question_type, exclude: item.dependant.exclude }), (item.dependant.required && { required: true })), { props: __assign(__assign({}, (item.dependant.options && { values: item.dependant.options })), { name: item.dependant.field_name }), name: item.dependant.field_name }), (item.dependant.dependant && {
                dependant: __assign(__assign(__assign(__assign(__assign({ title: item.dependant.dependant.question, rule: item.dependant.dependant.rule, recordId: (_b = item.dependant.dependant) === null || _b === void 0 ? void 0 : _b.record_id }, (item.dependant.dependant.required && { required: true })), (item.dependant.dependant.default_option && {
                    default_option: item.dependant.dependant.default_option
                })), { type: item.dependant.dependant.question_type, exclude: item.dependant.dependant.exclude }), (item.dependant.dependant.required && { required: true })), { props: __assign(__assign({}, (item.dependant.dependant.options && {
                        values: item.dependant.dependant.options
                    })), { name: item.dependant.dependant.field_name }), name: item.dependant.dependant.field_name })
            }))
        })), { order: item.priority, props: __assign(__assign({}, (item.options && { values: item.options })), { name: item.field_name }), name: item.field_name });
    })
        .sort(function (a, b) { return (a.order > b.order ? 1 : -1); });
};

var useQuestionsLocal = function (_a) {
    var componentsPageFilter = _a.componentsPageFilter, db = _a.db, platform = _a.platform;
    var _b = __read(useState([]), 2), questions = _b[0], setQuestions = _b[1];
    var _c = __read(useState([]), 2), components = _c[0], setComponents = _c[1];
    var _d = __read(useState(false), 2), loading = _d[0], setLoading = _d[1];
    var _e = __read(useState(null), 2), error = _e[0], setError = _e[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getQuestions(db, platform)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setQuestions(response);
                        setComponents(getDataModel(response
                            .filter(function (question) {
                            if (componentsPageFilter) {
                                return question.page === componentsPageFilter;
                            }
                            return true;
                        })
                            .map(function (question) { return (__assign({}, question)); })));
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load questions");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [db, setLoading, setQuestions]);
    return {
        components: components,
        error: error,
        loading: loading,
        questions: questions
    };
};

var questionsState = atom({
    default: [],
    key: "questions"
});
var componentsState = atom({
    default: [],
    key: "components"
});
var useQuestions = function (_a) {
    var componentsPageFilter = _a.componentsPageFilter, db = _a.db, platform = _a.platform;
    var _b = __read(useRecoilState(questionsState), 2), questions = _b[0], setQuestions = _b[1];
    var _c = __read(useRecoilState(componentsState), 2), components = _c[0], setComponents = _c[1];
    var _d = useQuestionsLocal({
        componentsPageFilter: componentsPageFilter,
        db: db,
        platform: platform
    }), error = _d.error, loading = _d.loading, questionsLocal = _d.questions, componentsLocal = _d.components;
    useEffect(function () {
        setQuestions(questionsLocal);
        setComponents(componentsLocal);
    }, [questionsLocal, componentsLocal]);
    return {
        components: components,
        error: error,
        loading: loading,
        questions: questions
    };
};

var getSalesOwners = function (db) { return __awaiter(void 0, void 0, void 0, function () {
    var q, salesOwnersSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "sales_owners"), where("canAcceptSales", "==", true), orderBy("name"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                salesOwnersSnapshot = _a.sent();
                return [2 /*return*/, salesOwnersSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var salesOwnersState = atom({
    default: [],
    key: "salesOwners"
});
var useSalesOwners = function (_a) {
    var db = _a.db;
    var _b = __read(useRecoilState(salesOwnersState), 2), salesOwners = _b[0], setSalesOwners = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getSalesOwners(db)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setSalesOwners(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load sales owners");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [db, setLoading, setSalesOwners]);
    return {
        error: error,
        loading: loading,
        salesOwners: salesOwners
    };
};

var getServicePricing = function () { return __awaiter(void 0, void 0, void 0, function () {
    var q, servicePricingSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "service_pricing"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                servicePricingSnapshot = _a.sent();
                return [2 /*return*/, servicePricingSnapshot.docs.map(function (doc) { return doc.data(); })];
        }
    });
}); };

var servicePricingState = atom({
    default: [],
    key: "servicePricing"
});
var useServicePricing = function () {
    var _a = __read(useRecoilState(servicePricingState), 2), servicePricing = _a[0], setServicePricing = _a[1];
    var _b = __read(useState(false), 2), loading = _b[0], setLoading = _b[1];
    var _c = __read(useState(null), 2), error = _c[0], setError = _c[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError("");
                        setLoading(true);
                        return [4 /*yield*/, getServicePricing()];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setServicePricing(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load service pricing");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [setLoading, setServicePricing]);
    return {
        error: error,
        loading: loading,
        servicePricing: servicePricing
    };
};

var termsAndConditionsState = atom({
    default: "",
    key: "termsAndConditions"
});
var useTermsAndConditions = function () {
    var _a, _b;
    var _c = __read(useRecoilState(termsAndConditionsState), 2), termsAndConditions = _c[0], setTermsAndConditions = _c[1];
    var _d = useErrorMessages({
        platform: "book.litta"
    }), error = _d.error, errorMessages = _d.errorMessages, loading = _d.loading;
    var terms = (_b = (_a = errorMessages.find(function (item) { return item.Name === "terms_and_conditions"; })) === null || _a === void 0 ? void 0 : _a.error_message) !== null && _b !== void 0 ? _b : "";
    useEffect(function () {
        setTermsAndConditions(terms);
    }, [terms]);
    return {
        error: error,
        loading: loading,
        termsAndConditions: termsAndConditions
    };
};

var getTimeSlotHeaders = function (db) { return __awaiter(void 0, void 0, void 0, function () {
    var q, timeSlotHeadersSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "time_slot_headers"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                timeSlotHeadersSnapshot = _a.sent();
                return [2 /*return*/, timeSlotHeadersSnapshot.docs.map(function (doc) {
                        return doc.data();
                    })];
        }
    });
}); };

var timeSlotHeadersState = atom({
    default: [],
    key: "timeSlotHeaders"
});
var useTimeSlotHeaders = function (_a) {
    var db = _a.db;
    var _b = __read(useRecoilState(timeSlotHeadersState), 2), timeSlotHeaders = _b[0], setTimeSlotHeaders = _b[1];
    var _c = __read(useState(false), 2), loading = _c[0], setLoading = _c[1];
    var _d = __read(useState(null), 2), error = _d[0], setError = _d[1];
    useEffect(function () {
        var getData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError(null);
                        setLoading(true);
                        return [4 /*yield*/, getTimeSlotHeaders(db)];
                    case 1:
                        response = _a.sent();
                        setLoading(false);
                        setTimeSlotHeaders(response);
                        if (response) {
                            setError(null);
                        }
                        else {
                            setError("Unable to load time slot headers");
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        getData();
    }, [db, setLoading, setTimeSlotHeaders]);
    return {
        error: error,
        loading: loading,
        timeSlotHeaders: timeSlotHeaders
    };
};

function useUserAgent() {
    var _a = __read(useState(true), 2), isLoading = _a[0], setIsLoading = _a[1];
    var _b = __read(useState(), 2), userAgentString = _b[0], setUserAgentString = _b[1];
    var parsedUserAgent = UAParser(userAgentString);
    useEffect(function () {
        if (typeof window.navigator === "undefined")
            return;
        if (typeof window.navigator.userAgent === "undefined")
            return;
        setUserAgentString(window.navigator.userAgent);
        setIsLoading(false);
    }, []);
    return __assign({ isLoading: isLoading }, parsedUserAgent);
}

var getFormBuilderForms = function (db) { return __awaiter(void 0, void 0, void 0, function () {
    var queryConstraints, queryReference, formSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                queryConstraints = [];
                if (!(process.env.NODE_ENV === "development")) {
                    queryConstraints = [where("active", "==", true), orderBy("order", "asc")];
                }
                queryReference = query.apply(void 0, __spreadArray([collection(db, "form_builder_forms")], __read(queryConstraints), false));
                return [4 /*yield*/, getDocs(queryReference)];
            case 1:
                formSnapshot = _a.sent();
                return [2 /*return*/, formSnapshot.docs.map(function (doc) { return (__assign({ id: doc.id }, doc.data())); })];
        }
    });
}); };

var useGetFormList = function () {
    return useQuery(["formBuilderFormData", "all"], function () { return getFormBuilderForms(db); }, {
        staleTime: 1000 * 60 * 60 * 24,
        cacheTime: 1000 * 60 * 60 * 24
    });
};

var getFormDataBySlug = function (db, slug) { return __awaiter(void 0, void 0, void 0, function () {
    var queryReference, formSnapshot, response;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                if (typeof slug === "undefined")
                    return [2 /*return*/, Promise.reject(new Error("Slug is undefined"))];
                queryReference = query(collection(db, "form_builder_forms"), where("url_slug", "==", slug));
                return [4 /*yield*/, getDocs(queryReference)];
            case 1:
                formSnapshot = _a.sent();
                response = formSnapshot.docs.map(function (doc) { return (__assign({ id: doc.id }, doc.data())); });
                return [2 /*return*/, __assign({}, response)];
        }
    });
}); };

var getFormBuilderFields = function (db, formName) { return __awaiter(void 0, void 0, void 0, function () {
    var queryConstraints, queryReference, fieldsSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                if (typeof formName === "undefined")
                    return [2 /*return*/, Promise.reject(new Error("Form Name is undefined"))];
                queryConstraints = [
                    where("forms", "array-contains", formName),
                    orderBy("order", "asc")
                ];
                queryReference = query.apply(void 0, __spreadArray([collection(db, "form_builder_fields")], __read(queryConstraints), false));
                return [4 /*yield*/, getDocs(queryReference)];
            case 1:
                fieldsSnapshot = _a.sent();
                return [2 /*return*/, fieldsSnapshot.docs.map(function (doc) { return (__assign({ id: doc.id }, doc.data())); })];
        }
    });
}); };

var useGetFormDataBySlug = function (slug) {
    var _a;
    var formQueryResponse = useQuery(["formBuilderFormData", { slug: slug }], function () { return getFormDataBySlug(db, slug); }, {
        enabled: Boolean(slug),
        staleTime: 1000 * 60 * 60 * 24,
        cacheTime: 1000 * 60 * 60 * 24
    });
    var formQueryData = (_a = formQueryResponse === null || formQueryResponse === void 0 ? void 0 : formQueryResponse.data) === null || _a === void 0 ? void 0 : _a[0];
    var fieldsQuery = useQuery(["form_builder_fields", formQueryData === null || formQueryData === void 0 ? void 0 : formQueryData.name], function () { return getFormBuilderFields(db, formQueryData === null || formQueryData === void 0 ? void 0 : formQueryData.name); }, {
        refetchOnWindowFocus: false,
        enabled: Boolean(formQueryData === null || formQueryData === void 0 ? void 0 : formQueryData.name)
    });
    var fieldsQueryData = fieldsQuery === null || fieldsQuery === void 0 ? void 0 : fieldsQuery.data;
    return {
        formQuery: __assign(__assign({}, formQueryResponse), { data: formQueryData }),
        fieldsQuery: __assign(__assign({}, fieldsQuery), { data: fieldsQueryData })
    };
};

var getFormDataByName = function (db, formName) { return __awaiter(void 0, void 0, void 0, function () {
    var queryReference, formSnapshot, response;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                if (typeof formName === "undefined")
                    return [2 /*return*/, Promise.reject(new Error("Form name is undefined"))];
                queryReference = query(collection(db, "form_builder_forms"), where("name", "==", formName));
                return [4 /*yield*/, getDocs(queryReference)];
            case 1:
                formSnapshot = _a.sent();
                response = formSnapshot.docs.map(function (doc) { return (__assign({ id: doc.id }, doc.data())); });
                return [2 /*return*/, __assign({}, response)];
        }
    });
}); };

var useGetFormDataByName = function (formName) {
    var _a;
    var formQueryResponse = useQuery(["formBuilderFormData", { name: formName }], function () { return getFormDataByName(db, formName); });
    var fieldsQueryResponse = useQuery(["form_builder_fields", { formName: formName }], function () { return getFormBuilderFields(db, formName); }, {
        refetchOnWindowFocus: false,
        enabled: Boolean(formName)
    });
    return {
        formQuery: __assign(__assign({}, formQueryResponse), { data: (_a = formQueryResponse === null || formQueryResponse === void 0 ? void 0 : formQueryResponse.data) === null || _a === void 0 ? void 0 : _a[0] }),
        fieldsQuery: __assign(__assign({}, fieldsQueryResponse), { data: fieldsQueryResponse === null || fieldsQueryResponse === void 0 ? void 0 : fieldsQueryResponse.data })
    };
};

var getServicePricingByType = function (db, type) { return __awaiter(void 0, void 0, void 0, function () {
    var queryReference, servicePricingSnapshot, response;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                if (typeof type === "undefined")
                    return [2 /*return*/, Promise.reject(new Error("Type is undefined"))];
                queryReference = query.apply(void 0, __spreadArray([collection(db, "service_pricing")], [
                    where("type", "==", type),
                    where("active", "==", true),
                    orderBy("sort_product_order", "asc")
                ], false));
                return [4 /*yield*/, getDocs(queryReference)];
            case 1:
                servicePricingSnapshot = _a.sent();
                response = servicePricingSnapshot.docs.map(function (doc) { return (__assign({ id: doc.id }, doc.data())); });
                return [2 /*return*/, response];
        }
    });
}); };

var useLoadIncrements = function () {
    var _a;
    var type = "load_increment";
    var loadIncrementResponse = useQuery(["servicePricing", type], function () { return getServicePricingByType(db, type); }, {
        staleTime: 1000 * 60 * 60 * 24,
        cacheTime: 1000 * 60 * 60 * 24
    });
    var data = (_a = loadIncrementResponse === null || loadIncrementResponse === void 0 ? void 0 : loadIncrementResponse.data) === null || _a === void 0 ? void 0 : _a[0];
    return __assign(__assign({}, loadIncrementResponse), { data: data });
};

var getFormsServicePricing = function (formNameSlug) { return __awaiter(void 0, void 0, void 0, function () {
    var q, servicePricingSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "service_pricing"), where("type", "==", "load_increment_form"), where("active", "==", true), orderBy("sort_product_order", "asc"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                servicePricingSnapshot = _a.sent();
                return [2 /*return*/, servicePricingSnapshot.docs.reduce(function (acc, item) {
                        // const filterByFormName = item
                        //   .get("form_builder_forms")
                        //   .find((formName) => formName === formNameSlug)
                        var filterByFormName = item.get("form_builder_forms");
                        if (filterByFormName) {
                            var findByFormName = filterByFormName.find(function (formName) { return formName === formNameSlug; });
                            if (findByFormName) {
                                acc.push(item.data());
                            }
                        }
                        // if (filterByFormName) {
                        //   acc.push(item.data())
                        // }
                        return acc;
                    }, [])];
        }
    });
}); };

var useFormsServicePricing = function (formNameSlug, options) {
    return useQuery(["formsServicePricing"], function () { return getFormsServicePricing(formNameSlug); }, options);
};

var getItemGrouping = function (formNameSlug) { return __awaiter(void 0, void 0, void 0, function () {
    var q, servicePR, itemGroups, servicePricingSnapshot, servicePRfilter, itemsGroupsResponse;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "add_item_grouping"), where("type", "==", "additional"), where("form_builder_forms", "array-contains", formNameSlug), orderBy("sort_order", "asc"));
                servicePR = query(collection(db, "service_pricing"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                itemGroups = _a.sent();
                return [4 /*yield*/, getDocs(servicePR)];
            case 2:
                servicePricingSnapshot = _a.sent();
                servicePRfilter = servicePricingSnapshot.docs.map(function (doc) {
                    return __assign(__assign({}, doc.data()), { docId: doc.id });
                });
                itemsGroupsResponse = [];
                itemGroups.docs.forEach(function (document) {
                    var itemGroup = {
                        icon: document.get("icon"),
                        icon_v2: document.get("icon_v2"),
                        group_title: document.get("group_title")
                    };
                    var servicePricing = [];
                    document.get("service_pricing").forEach(function (serviceP) { return __awaiter(void 0, void 0, void 0, function () {
                        return __generator(this, function (_a) {
                            servicePRfilter.forEach(function (servicePricingItem) {
                                if (serviceP === servicePricingItem.docId) {
                                    servicePricing.push(__assign({ servicePricingId: servicePricingItem.docId, sort_product_order: servicePricingItem.sort_product_order, litta_sku: servicePricingItem.LITTA_SKU, litta_service_linked: servicePricingItem.customer_service_title, price_inc_vat: servicePricingItem.price_inc_vat, count: 0 }, servicePricingItem));
                                }
                            });
                            return [2 /*return*/];
                        });
                    }); });
                    servicePricing.sort(function (a, b) {
                        return a.sort_product_order > b.sort_product_order ? 1 : -1;
                    });
                    itemGroup.service_pricing = servicePricing;
                    itemsGroupsResponse.push(itemGroup);
                });
                return [2 /*return*/, itemsGroupsResponse];
        }
    });
}); };

var useFormsItemsGroups = function (formNameSlug, options) {
    return useQuery(["formsItemsGroup"], function () { return getItemGrouping(formNameSlug); }, options);
};

var getBookOrders = function (bookOrderId) { return __awaiter(void 0, void 0, void 0, function () {
    var docRef, docSnap;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                docRef = doc(db, "book_orders", bookOrderId);
                return [4 /*yield*/, getDoc(docRef)];
            case 1:
                docSnap = _a.sent();
                if (docSnap.exists()) {
                    return [2 /*return*/, docSnap.data()];
                }
                return [2 /*return*/, "No such document!"];
        }
    });
}); };

var useBookOrders = function (bookOrderId, options) {
    return useQuery(["bookOrders"], function () { return getBookOrders(bookOrderId); }, options);
};

var getFormBuilderFieldOrder = function () { return __awaiter(void 0, void 0, void 0, function () {
    var q, formBuilderFieldOrderSnapshot;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                q = query(collection(db, "form_builder_field_order"), orderBy("order"));
                return [4 /*yield*/, getDocs(q)];
            case 1:
                formBuilderFieldOrderSnapshot = _a.sent();
                return [2 /*return*/, formBuilderFieldOrderSnapshot.docs.map(function (doc) {
                        return doc.data();
                    })];
        }
    });
}); };

var useFormBuilderFieldOrder = function (options) { return useQuery(["formBuilderFieldOrder"], getFormBuilderFieldOrder, options); };

var apiKey = process.env.REACT_APP_GETADDRESSIO_API_KEY;
var getAddressAxiosInstance = axios.create({
    baseURL: "https://api.getaddress.io",
    params: {
        "api-key": apiKey
    }
});
// interceptor to add api-key without being overwritten by custom configs
getAddressAxiosInstance.interceptors.request.use(function (config) {
    var _a;
    config.params = (_a = config.params) !== null && _a !== void 0 ? _a : {};
    config.params["api-key"] = apiKey;
    return config;
}, function (error) {
    return Promise.reject(error);
});

var getAddressSuggestions = function (searchValue, customParams) {
    var params = customParams !== null && customParams !== void 0 ? customParams : null;
    var config = {
        method: "GET",
        url: "/autocomplete/".concat(searchValue),
        params: params
    };
    return getAddressAxiosInstance.request(config);
};

var getAddressById = function (addressId, params) {
    var config = {
        url: "/get/".concat(addressId),
        method: "GET",
        params: __assign({}, params)
    };
    return getAddressAxiosInstance.request(config);
};

// queryKey factory: https://tkdodo.eu/blog/effective-react-query-keys
var getAddressKeys = {
    all: ["get_address_io"],
    addresses: function () { return __spreadArray(__spreadArray([], __read(getAddressKeys.all), false), ["address"], false); },
    address: function (addressId) {
        return __spreadArray(__spreadArray([], __read(getAddressKeys.addresses()), false), [{ addressId: addressId }], false);
    },
    suggestionsAll: function () { return __spreadArray(__spreadArray([], __read(getAddressKeys.all), false), ["suggestions"], false); },
    suggestions: function (searchString) {
        return __spreadArray(__spreadArray([], __read(getAddressKeys.suggestionsAll()), false), [{ searchString: searchString }], false);
    }
};

var useGetAddressById = function (addressId) {
    var _a;
    var queryResponse = useQuery(getAddressKeys.address(addressId), function () { return getAddressById(addressId); }, {
        refetchOnWindowFocus: false,
        enabled: Boolean(addressId),
        staleTime: 1000 * 60 * 60 * 24 // 1 day
    });
    var data = (_a = queryResponse.data) === null || _a === void 0 ? void 0 : _a.data;
    return __assign(__assign({}, queryResponse), { data: data });
};

var useAddressAutocomplete = function (searchString, maxSize, allForPostCodes) {
    var _a;
    var searchStringLower = searchString.toLowerCase();
    var queryResponse = useQuery(getAddressKeys.suggestions(searchStringLower), function () {
        return getAddressSuggestions(searchStringLower, {
            top: maxSize,
            all: allForPostCodes
        });
    }, {
        refetchOnWindowFocus: false,
        enabled: searchString.length > 3,
        staleTime: 1000 * 60 * 60 * 24,
        queryHash: searchStringLower
    });
    var data = ((_a = queryResponse.data) === null || _a === void 0 ? void 0 : _a.data.suggestions) || [];
    return __assign(__assign({}, queryResponse), { data: data });
};

var useAddress = function () { return ({
    getAddressById: useGetAddressById,
    getSuggestions: useAddressAutocomplete
}); };

var subdomain = process.env.REACT_APP_PAY_CLOUDFUNCTIONS_DOMAIN_NAME;
var baseURL = "https://".concat(subdomain, ".cloudfunctions.net");
var cloudFunctionsAxiosInstance = axios.create({ baseURL: baseURL });

var postQuoteRequest = function (payload, config) {
    var response = cloudFunctionsAxiosInstance.request(__assign({ url: "/quote_request", method: "POST", data: payload }, config));
    var data = response.then(function (response) { return response === null || response === void 0 ? void 0 : response.data; });
    return __assign(__assign({}, response), { data: data });
};

var usePostQuoteRequest = function () {
    var mutationFn = function (payload) {
        var response = postQuoteRequest(payload);
        return response.data;
    };
    var mutation = useMutation(mutationFn, {
        onSuccess: function (response) {
            console.log("useMutation onSuccess response: ", response);
        },
        onError: function (error) {
            console.log("useMutation onError error: ", { error: error });
        }
    });
    return mutation;
};

var storage = getStorage();
var getStorageRef = function (path) {
    return ref(storage, path);
};
var uploadToCloudStorage = function (_a) {
    var name = _a.name, path = _a.path, file = _a.file, metadata = _a.metadata, onProgressStateChanged = _a.onProgressStateChanged, onError = _a.onError, onFileUploaded = _a.onFileUploaded;
    return __awaiter(void 0, void 0, void 0, function () {
        var ref, uploadTask;
        return __generator(this, function (_b) {
            ref = getStorageRef(path);
            uploadTask = uploadBytesResumable(ref, file, metadata);
            uploadTask.on("state_changed", function (snapshot) {
                onProgressStateChanged === null || onProgressStateChanged === void 0 ? void 0 : onProgressStateChanged(snapshot);
            }, function (error) {
                onError === null || onError === void 0 ? void 0 : onError(error);
                // A full list of error codes is available at
                // https://firebase.google.com/docs/storage/web/handle-errors
                switch (error.code) {
                                    }
            }, function () {
                getDownloadURL(uploadTask.snapshot.ref).then(function (downloadURL) {
                    var localURL = URL.createObjectURL(file);
                    console.log("File available at", localURL);
                    return onFileUploaded === null || onFileUploaded === void 0 ? void 0 : onFileUploaded({
                        rawFile: file,
                        name: name,
                        downloadURL: downloadURL,
                        localURL: localURL
                    });
                });
            });
            return [2 /*return*/, { uploadResult: uploadTask }];
        });
    });
};

export { getStorageRef, storage, uploadToCloudStorage, useAddress, useBookOrderByWebReference, useBookOrders, useBookingTypes, useCalculateTotal, useCalculateTotalWithCoupons, useCallOutFee, useDismantleDisconnectService, useEmailVerify, useErrorMessages, useFailReasons, useFormBuilderFieldOrder, useFormsItemsGroups, useFormsServicePricing, useGetFormDataByName, useGetFormDataBySlug, useGetFormList, useHolidays, useImagesRequest, useLoadIncrements, usePageContent, usePageHeaders, usePayouts, usePostCodesSurge, usePostQuoteRequest, usePromoCode, usePurchaseDataLayer, useQuestions, useQuestionsLocal, useSalesOwners, useServicePricing, useTermsAndConditions, useTimeSlotHeaders, useUserAgent };
